import TransactionDetails from '@components/transaction-details/transaction-details.component'
import { Button, HistoryTransaction, Loader, Modal } from '@hub/shared'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './modal-popup-antecipation.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  openModal: boolean
  transactionObject: HistoryTransaction | null
  loading?: boolean
  openOnboarding?: () => any
  nextStep?: () => any
  closeModal?: () => any
}

export default ({ openModal, transactionObject, loading, openOnboarding, nextStep, closeModal }: Props) => {
  const [t] = useTranslation('modalPopupAntecipation')

  const [width, setWidth] = useState(window?.innerWidth)
  const [height, setHeight] = useState(window?.innerHeight)
  const [activeStep, setActiveStep] = useState(0)

  const handleCloseModal = useCallback(() => {
    if (closeModal && typeof closeModal === 'function') {
      closeModal()
    }
  }, [closeModal])

  const handleNextStep = useCallback(() => {
    if (nextStep && typeof nextStep === 'function') {
      nextStep()
    }
  }, [nextStep])

  const formatDate = (date: any) => {
    return date?.split('-').reverse().join('/')
  }

  const formatValue = (value?: number) => {
    return value?.toLocaleString('pt-br', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 2,
    })
  }

  const steps = useMemo(
    () => [
      {
        content: (
          <div className="content">
            <span
              dangerouslySetInnerHTML={{
                __html: t('receivable', {
                  value: formatValue(transactionObject?.netValue),
                  date: formatDate(transactionObject?.dueDate),
                }),
              }}
            />
            <i className="fa-regular fa-hand-holding-dollar" style={{ fontSize: '8.5rem' }}></i>
            <span
              dangerouslySetInnerHTML={{
                __html: t('wantAntecipate', { date: formatDate(transactionObject?.anticipationDueDate) }),
              }}
            />
            <Button
              onClick={() => openOnboarding && typeof openOnboarding === 'function' && openOnboarding()}
              color="var(--transparent)"
              borderColor="var(--text_color)"
              textColor="var(--text_color)"
            >
              {t('whatIsAnticipation')}
            </Button>
          </div>
        ),
        cancelButton: {
          text: t('cancel'),
          function: () => handleCloseModal(),
        },
        confirmButton: {
          text: t('confirm'),
          function: () => setActiveStep(activeStep + 1),
        },
      },
      {
        content: (
          <div className="content-extract">
            <div className="extract mb-5">
              <TransactionDetails title={t('extract.originalDate')} value={formatDate(transactionObject?.dueDate)} isMoney={false} />
              <br />
              <TransactionDetails title={t('extract.originalValue')} value={transactionObject?.netValue} />
              <hr />
              <TransactionDetails
                title={t('extract.aliquot')}
                aliquot={transactionObject?.anticipation?.aliquot}
                value={formatValue(transactionObject?.anticipation?.value)}
                inOrOut="out"
              />
              <hr />
              <TransactionDetails
                title={t('extract.value')}
                value={
                  transactionObject?.netValue
                    ? formatValue(transactionObject?.netValue - transactionObject?.netValue * transactionObject?.anticipation?.aliquot)
                    : null
                }
                inOrOut="in"
              />
            </div>
            <div className="wantReceive">
              <span
                dangerouslySetInnerHTML={{
                  __html: t('wantReceive', {
                    value: transactionObject?.netValue
                      ? formatValue(transactionObject?.netValue - transactionObject?.netValue * transactionObject?.anticipation?.aliquot)
                      : null,

                    date: formatDate(transactionObject?.anticipationDueDate),
                  }),
                }}
              />
            </div>
          </div>
        ),
        cancelButton: {
          text: t('no'),
          function: () => handleCloseModal(),
        },
        confirmButton: {
          text: t('yes'),
          function: () => handleNextStep(),
        },
      },
    ],
    [activeStep, transactionObject, openOnboarding, t, handleCloseModal, handleNextStep]
  )

  useLayoutEffect(() => {
    const updateWidth = () => {
      setWidth(window?.innerWidth)
    }
    const updateHeight = () => {
      setHeight(window?.innerHeight)
    }
    window.addEventListener('resize', updateWidth)
    window.addEventListener('resize', updateHeight)
    return () => {
      window.removeEventListener('resize', updateWidth)
      window.removeEventListener('resize', updateHeight)
    }
  }, [])

  return (
    <Modal
      className="modal-popup-antecipation"
      open={openModal}
      buttonClose={!loading}
      onCloseChange={() => handleCloseModal()}
      modalSize="lg"
      forceMobileNoMargin
      header={<h5 className="m-0">{t('title')}</h5>}
      footer={
        <div className="menu-modal col-24 display-flex justify-content-space-between">
          <Button
            id="cancel-button-modal-popup-antecipation"
            onClick={() => steps[activeStep].cancelButton.function()}
            color="transparent"
            textColor="var(--gray)"
            borderColor="var(--gray)"
            disabled={loading}
          >
            <span>{steps[activeStep].cancelButton.text}</span>
          </Button>
          <Button id="confirm-button-modal-popup-antecipation" onClick={() => steps[activeStep].confirmButton.function()} loading={loading}>
            <span>{steps[activeStep].confirmButton.text}</span>
          </Button>
        </div>
      }
    >
      <div className="modal-popup-antecipation-content" style={width < 992 ? { height: `calc(${height}px - 143px)` } : { height: '70vh' }}>
        {!transactionObject?.transactionId ? (
          <div className="display-flex justify-content-center align-items-center" style={{ height: '100%' }}>
            <Loader />
          </div>
        ) : (
          steps[activeStep]?.content
        )}
      </div>
    </Modal>
  )
}
